import { FlexBox, Icon, Typography } from '@vp/swan';
import { getOrCreateFormattedTimezone, getTimeOfDayIcon } from '@99designs/design-services-common';
import { parseAppointmentValue } from '@99designs/feature-design-services-brief';
import { __, getCurrentCulture } from '@99designs/i18n';
import type { ReviewMethodReply } from './parseReply';

type ReviewMethodMessageViewProps = {
    data: ReviewMethodReply;
};

/**
 * The settled, read-only answer for a review-method turn.
 *
 * Standard: icon + label only.
 * DesignLive: icon + label + formatted date, time, timezone, and optional designer name.
 *
 * Matches the legacy renderMessageView exactly:
 * - Date:          "Wednesday 9 September 2026"  (locale-aware, day-before-month)
 * - Session starts: "5:00 pm ☀️"                 (lowercase am/pm + day/night icon)
 * - Timezone:      "Melbourne, Australia (AEST)"  (getOrCreateFormattedTimezone.displayName)
 * - Designer:      "Alice Smith"                  (only when a specific designer was chosen)
 */
export function ReviewMethodMessageView({ data }: ReviewMethodMessageViewProps) {
    const isDesignLive = data.requestType === 'DesignLive';

    let date: string | undefined;
    let time: string | undefined;
    let timezoneDisplay: string | undefined;
    let designerName: string | undefined;

    if (isDesignLive) {
        const appointment = parseAppointmentValue(data.appointmentTime);
        designerName = appointment.designerName;

        if (appointment.time) {
            const dt = new Date(appointment.time);
            const tz = appointment.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            const locale = getCurrentCulture();

            date = dt.toLocaleDateString(locale, {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone: tz,
            });

            time = `${dt.toLocaleTimeString(locale, {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: tz,
            })} ${getTimeOfDayIcon(tz, dt)}`;

            timezoneDisplay = getOrCreateFormattedTimezone(tz, dt).displayName;
        }
    }

    const isVideoConfirmation = isDesignLive && !!date && !!time && !!timezoneDisplay;
    const iconType = isDesignLive ? 'videoChat' : 'mail';
    const title = isDesignLive
        ? __('Schedule a live video call')
        : __('Review online in your own time');

    return (
        <FlexBox
            flexDirection="column"
            gap={4}
            backgroundColor="standard"
            paddingY={isVideoConfirmation ? 5 : 4}
            paddingX={5}
            style={{
                border: isVideoConfirmation
                    ? undefined
                    : '1px solid var(--swan-sem-color-border-subtle)',
                borderRadius: isVideoConfirmation
                    ? 'var(--swan-sem-border-radius-strong)'
                    : 'var(--swan-sem-border-radius-standard)',
                minWidth: isVideoConfirmation ? '280px' : undefined,
                overflow: 'hidden',
            }}
        >
            <FlexBox alignItems="center" gap={3}>
                <Icon iconType={iconType} size="standard" />
                <Typography fontSkin="body-standard-bold">{title}</Typography>
            </FlexBox>
            {isVideoConfirmation && (
                <FlexBox flexDirection="column" gap={2}>
                    <FlexBox gap={2}>
                        <Typography
                            fontSkin="body-standard"
                            style={{ width: '120px', flexShrink: 0 }}
                        >
                            {__('Date:')}
                        </Typography>
                        <Typography fontSkin="body-standard">{date}</Typography>
                    </FlexBox>
                    <FlexBox gap={2}>
                        <Typography
                            fontSkin="body-standard"
                            style={{ width: '120px', flexShrink: 0 }}
                        >
                            {__('Session starts:')}
                        </Typography>
                        <Typography fontSkin="body-standard">{time}</Typography>
                    </FlexBox>
                    <FlexBox gap={2}>
                        <Typography
                            fontSkin="body-standard"
                            style={{ width: '120px', flexShrink: 0 }}
                        >
                            {__('Timezone:')}
                        </Typography>
                        <Typography fontSkin="body-standard">{timezoneDisplay}</Typography>
                    </FlexBox>
                    {designerName && (
                        <FlexBox gap={2}>
                            <Typography
                                fontSkin="body-standard"
                                style={{ width: '120px', flexShrink: 0 }}
                            >
                                {__('Designer:')}
                            </Typography>
                            <Typography fontSkin="body-standard">{designerName}</Typography>
                        </FlexBox>
                    )}
                </FlexBox>
            )}
        </FlexBox>
    );
}
